import React, { useMemo, useState } from 'react';
import { mockLeads } from '../utils/mockData';
import { Lead, EligibilityStatus } from '../types';
import { Card } from './ui/Card';
import { LeadTable } from './LeadTable';
import { CancelledLeadsTable } from './CancelledLeadsTable';
import { SentLeadsTable } from './SentLeadsTable';
import { SidePanel } from './SidePanel';
import { SearchIcon, InfoIcon, UsersIcon, XCircleIcon, CheckCircleIcon, CalendarIcon, SendIcon } from 'lucide-react';
const CANCELLED_STATUSES = ['Working', 'Nurturing', 'Converted'];
type TabValue = 'eligible' | 'cancelled' | 'sent';
type TimeframeValue = 'today' | 'yesterday' | 'wtd' | 'mtd';
function getTimeframeRange(timeframe: TimeframeValue): {
  start: Date;
  end: Date;
} {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
  switch (timeframe) {
    case 'today':
      return {
        start: todayStart,
        end: todayEnd
      };
    case 'yesterday':
      {
        const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
        return {
          start: yesterdayStart,
          end: todayStart
        };
      }
    case 'wtd':
      {
        const day = todayStart.getDay();
        const diff = day === 0 ? 6 : day - 1;
        const weekStart = new Date(todayStart.getTime() - diff * 24 * 60 * 60 * 1000);
        return {
          start: weekStart,
          end: todayEnd
        };
      }
    case 'mtd':
      {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return {
          start: monthStart,
          end: todayEnd
        };
      }
  }
}
const TIMEFRAME_LABELS: Record<TimeframeValue, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  wtd: 'WTD',
  mtd: 'MTD'
};
export function Dashboard() {
  const [leads, setLeads] = useState<Lead[]>(mockLeads);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<TabValue>('eligible');
  const [timeframe, setTimeframe] = useState<TimeframeValue>('today');
  // Side Panel State
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [selectedEligibility, setSelectedEligibility] = useState<EligibilityStatus>('Eligible');
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  // Filter by timeframe and search, then split into 3 buckets
  const {
    pendingLeads,
    cancelledLeads,
    sentLeads
  } = useMemo(() => {
    const {
      start,
      end
    } = getTimeframeRange(timeframe);
    const pending: Lead[] = [];
    const cancelled: Lead[] = [];
    const sent: Lead[] = [];
    leads.forEach(lead => {
      const enteredDate = new Date(lead.timeEntered);
      if (enteredDate < start || enteredDate >= end) return;
      const matchesSearch = searchQuery === '' || lead.name.toLowerCase().includes(searchQuery.toLowerCase()) || lead.phone.includes(searchQuery) || lead.email.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return;
      if (lead.status === 'Sent to Five9') {
        sent.push(lead);
      } else if (CANCELLED_STATUSES.includes(lead.status)) {
        cancelled.push(lead);
      } else {
        pending.push(lead);
      }
    });
    return {
      pendingLeads: pending,
      cancelledLeads: cancelled,
      sentLeads: sent
    };
  }, [leads, searchQuery, timeframe]);
  // Derived Metrics
  const metrics = useMemo(() => {
    return {
      total: pendingLeads.length + cancelledLeads.length + sentLeads.length,
      eligible: pendingLeads.length,
      sent: sentLeads.length,
      cancelled: cancelledLeads.length
    };
  }, [pendingLeads, cancelledLeads, sentLeads]);
  const handleViewDetails = (lead: Lead, eligibility: EligibilityStatus) => {
    setSelectedLead(lead);
    setSelectedEligibility(eligibility);
    setIsPanelOpen(true);
  };
  const handleAction = (leadId: string, action: 'send' | 'cancel') => {
    if (action === 'send') {
      setLeads(current => current.map(l => l.id === leadId ? {
        ...l,
        status: 'Sent to Five9' as const,
        activities: [...l.activities, {
          id: `sent-${Date.now()}`,
          type: 'system' as const,
          description: 'Lead manually sent to Five9 dialer',
          timestamp: new Date().toISOString()
        }]
      } : l));
    } else {
      setLeads(current => current.filter(l => l.id !== leadId));
    }
  };
  const tabs: {
    value: TabValue;
    label: string;
    count: number;
    dotColor: string;
  }[] = [{
    value: 'eligible',
    label: 'Eligible Leads',
    count: pendingLeads.length,
    dotColor: 'bg-green-500'
  }, {
    value: 'sent',
    label: 'Sent to Five9',
    count: sentLeads.length,
    dotColor: 'bg-blue-500'
  }, {
    value: 'cancelled',
    label: 'Cancelled',
    count: cancelledLeads.length,
    dotColor: 'bg-red-400'
  }];
  return <div className="min-h-screen bg-[#F9FAFB] text-gray-900 font-sans pb-12" data-id="element-33">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30" data-id="element-34">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-35">
          <div data-id="element-36">
            <h1 className="text-xl font-semibold text-gray-900" data-id="element-37">
              Pending Lead Queue
            </h1>
            <p className="text-sm text-gray-500" data-id="element-38">
              Leads awaiting Five9 enrollment (10-minute buffer window)
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200" data-id="element-39">
            <InfoIcon className="w-4 h-4 text-blue-500" data-id="element-40" />
            Leads are automatically evaluated after 10 minutes
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-41">
        {/* KPI Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-id="element-42">
          <Card className="p-5" data-id="element-43">
            <div className="flex items-center gap-3 text-gray-500 mb-2" data-id="element-44">
              <UsersIcon className="w-5 h-5" data-id="element-45" />
              <h3 className="text-sm font-medium" data-id="element-46">Total Leads</h3>
            </div>
            <p className="text-3xl font-bold text-gray-900" data-id="element-47">{metrics.total}</p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-48">
              {TIMEFRAME_LABELS[timeframe]}
            </p>
          </Card>
          <Card className="p-5" data-id="element-49">
            <div className="flex items-center gap-3 text-green-600 mb-2" data-id="element-50">
              <CheckCircleIcon className="w-5 h-5" data-id="element-51" />
              <h3 className="text-sm font-medium" data-id="element-52">Eligible for Send</h3>
            </div>
            <p className="text-3xl font-bold text-gray-900" data-id="element-53">
              {metrics.eligible}
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-54">
              {TIMEFRAME_LABELS[timeframe]}
            </p>
          </Card>
          <Card className="p-5" data-id="element-55">
            <div className="flex items-center gap-3 text-blue-600 mb-2" data-id="element-56">
              <SendIcon className="w-5 h-5" data-id="element-57" />
              <h3 className="text-sm font-medium" data-id="element-58">Total Sent</h3>
            </div>
            <p className="text-3xl font-bold text-gray-900" data-id="element-59">{metrics.sent}</p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-60">
              {TIMEFRAME_LABELS[timeframe]}
            </p>
          </Card>
          <Card className="p-5" data-id="element-61">
            <div className="flex items-center gap-3 text-red-500 mb-2" data-id="element-62">
              <XCircleIcon className="w-5 h-5" data-id="element-63" />
              <h3 className="text-sm font-medium" data-id="element-64">Cancelled During Window</h3>
            </div>
            <p className="text-3xl font-bold text-gray-900" data-id="element-65">
              {metrics.cancelled}
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-66">
              {TIMEFRAME_LABELS[timeframe]}
            </p>
          </Card>
        </div>

        {/* Main Card with Tabs */}
        <Card className="flex flex-col" data-id="element-67">
          {/* Toolbar: Timeframe + Search */}
          <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white" data-id="element-68">
            <div className="flex items-center gap-2" data-id="element-69">
              <CalendarIcon className="w-4 h-4 text-gray-400" data-id="element-70" />
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5" data-id="element-71">
                {(Object.keys(TIMEFRAME_LABELS) as TimeframeValue[]).map(tf => <button key={tf} onClick={() => setTimeframe(tf)} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${timeframe === tf ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-72">
                      {TIMEFRAME_LABELS[tf]}
                    </button>)}
              </div>
            </div>
            <div className="relative max-w-sm w-full" data-id="element-73">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-74">
                <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-75" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-shadow" placeholder="Search by name, phone, or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-76" />
            </div>
          </div>

          {/* Tab Bar */}
          <div className="px-4 bg-white border-b border-gray-200" data-id="element-77">
            <nav className="flex gap-6" aria-label="Tabs" data-id="element-78">
              {tabs.map(tab => <button key={tab.value} onClick={() => setActiveTab(tab.value)} className={`relative flex items-center gap-2 py-3 text-sm font-medium transition-colors ${activeTab === tab.value ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-79">
                  <div className={`w-2 h-2 rounded-full ${tab.dotColor}`} data-id="element-80" />
                  {tab.label}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === tab.value ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-81">
                    {tab.count}
                  </span>
                  {activeTab === tab.value && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900 rounded-full" data-id="element-82" />}
                </button>)}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'eligible' && <LeadTable leads={pendingLeads} onViewDetails={handleViewDetails} onAction={handleAction} data-id="element-83" />}
          {activeTab === 'sent' && <SentLeadsTable leads={sentLeads} onViewDetails={handleViewDetails} data-id="element-84" />}
          {activeTab === 'cancelled' && <CancelledLeadsTable leads={cancelledLeads} onViewDetails={handleViewDetails} onAction={handleAction} data-id="element-85" />}
        </Card>
      </main>

      <SidePanel isOpen={isPanelOpen} onClose={() => setIsPanelOpen(false)} lead={selectedLead} eligibility={selectedEligibility} data-id="element-86" />
    </div>;
}